import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
} from '@/shared/components';
import { useApi, toApiError, type CreateUserRequest } from '@/shared/api';
import { ROLES, type Role } from '@/shared/auth';
import { userKeys } from './queries';
import { InviteAssignmentFields, type InviteAssignment } from './InviteAssignmentFields';

const schema = z.object({
  firstName: z.string().min(1, 'Pflichtfeld'),
  lastName: z.string().min(1, 'Pflichtfeld'),
  email: z.string().email('Ungültige E-Mail'),
  role: z.enum([
    ROLES.WorkspaceAdmin,
    ROLES.Staff,
    ROLES.Housekeeping,
    ROLES.HousekeepingManager,
    ROLES.Owner,
    ROLES.Guest,
  ]),
});

type Values = z.infer<typeof schema>;

// Nur diese Rollen dürfen bei der Einladung Firma/Properties direkt zugewiesen bekommen (Backend-Guard C2).
const ASSIGNABLE_ROLES: readonly Role[] = [
  ROLES.Owner,
  ROLES.Housekeeping,
  ROLES.HousekeepingManager,
];
const EMPTY_ASSIGNMENT: InviteAssignment = { companyId: '', propertyIds: [] };

export function UserCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [assignment, setAssignment] = useState<InviteAssignment>(EMPTY_ASSIGNMENT);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: '', lastName: '', email: '', role: ROLES.Staff },
  });

  const role = watch('role');
  const showAssignment = ASSIGNABLE_ROLES.includes(role);

  const closeAndReset = () => {
    reset();
    setAssignment(EMPTY_ASSIGNMENT);
    onOpenChange(false);
  };

  const create = useMutation({
    mutationFn: api.users.create,
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success(`Einladung an ${created.email} versendet`);
      closeAndReset();
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  const submit = (v: Values) => {
    const body: CreateUserRequest = { ...v };
    if (ASSIGNABLE_ROLES.includes(v.role)) {
      if (assignment.companyId) body.companyId = assignment.companyId;
      if (assignment.propertyIds.length > 0) body.propertyIds = assignment.propertyIds;
    }
    return create.mutateAsync(body);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : closeAndReset())}>
      <DialogContent>
        <form onSubmit={handleSubmit(submit)}>
          <DialogHeader>
            <DialogTitle>Neuen Benutzer einladen</DialogTitle>
            <DialogDescription>
              Eine Einladung mit Aktivierungs-Link wird per E-Mail verschickt. Der Benutzer setzt
              sein Passwort selbst.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Vorname" error={errors.firstName?.message} required>
                <Input {...register('firstName')} autoFocus />
              </FormField>
              <FormField label="Nachname" error={errors.lastName?.message} required>
                <Input {...register('lastName')} />
              </FormField>
              <FormField
                label="E-Mail"
                error={errors.email?.message}
                required
                className="md:col-span-2"
              >
                <Input type="email" {...register('email')} placeholder="vorname@stayon.local" />
              </FormField>
              <FormField
                label="Rolle"
                error={errors.role?.message}
                required
                className="md:col-span-2"
              >
                <Select {...register('role')}>
                  <option value={ROLES.WorkspaceAdmin}>
                    Workspace-Administrator — voller Zugriff im Workspace
                  </option>
                  <option value={ROLES.Staff}>Mitarbeiter — Apartments + Buchungen</option>
                  <option value={ROLES.Housekeeping}>Reinigung — zugewiesene Apartments</option>
                  <option value={ROLES.HousekeepingManager}>
                    Reinigungsleitung — Cleaning-Management
                  </option>
                  <option value={ROLES.Owner}>Kunde — nur zugewiesene Apartments</option>
                  <option value={ROLES.Guest}>Gast — externer Gast (Phase 2)</option>
                </Select>
              </FormField>
              {showAssignment && (
                <div className="md:col-span-2">
                  <InviteAssignmentFields value={assignment} onChange={setAssignment} />
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeAndReset}>
              Abbrechen
            </Button>
            <Button type="submit" variant="brand" loading={create.isPending}>
              Einladen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
